import type { ImportRow, ImportedMember } from './csvImport';
import { hasAddress } from './members';
import { isActiveMember } from './membership';
import { newMember } from './transitions';
import type { Permissions } from './permissions';
import type { AvailState, Member, Ministry } from './types';

/**
 * Edição de membros em lote (o modo Editor de Membros e a etapa "Atribuir" da importação):
 * as funções habilitadas e a disponibilidade de vários membros de uma vez.
 *
 * A tela edita cópias dos membros (o rascunho, como o do perfil) com `withRole` e
 * `withAvailability`; `memberEdits` tira a diferença para o "Salvar", `memberEditError`
 * confere com as mesmas regras do perfil e `applyMemberEdits` grava — na store e no
 * servidor (`POST /members/bulk`). Depois de gravar, `releaseRoleLeaders` tira a liderança
 * de quem saiu da função, como no "Salvar" do perfil.
 */

/**
 * Os campos que a planilha completa em quem já está cadastrado (a linha "É a mesma
 * pessoa"): só entram onde o cadastro está vazio — nome e sexo ficam os do cadastro.
 */
export const FILLABLE_FIELDS = [
  'nickname',
  'birthDate',
  'email',
  'phone',
  'civilStatus',
  'profession',
  'churchStatus',
  'memberSince',
  'joinedVia',
  'baptismDate',
  'originChurch',
  'cpf',
  'address',
  'emergency',
] as const satisfies readonly (keyof Member & keyof ImportedMember)[];

export type FillableField = (typeof FILLABLE_FIELDS)[number];

export type MemberFill = Partial<Pick<Member, FillableField>>;

/** O que muda num membro: as funções inteiras (por ministério) e/ou a disponibilidade. */
export interface MemberEdit {
  memberId: string;
  /** ministryId → roleIds, como em `Member.roles`: o que vale depois da edição. */
  roles?: Record<string, string[]>;
  /** As 21 posições, como em `Member.availability`. */
  availability?: AvailState[];
  /** Campos vazios do cadastro que a planilha completa (só o Administrador). */
  fill?: MemberFill;
}

/** Por que o lote não pode ser gravado; `forbidden` quando falta permissão. */
export interface MemberEditProblem {
  message: string;
  forbidden: boolean;
}

/** O campo está vazio no cadastro? (O endereço e o contato contam inteiros.) */
export function isBlank(member: Member, field: FillableField): boolean {
  if (field === 'address') return !hasAddress(member.address);
  if (field === 'emergency') return !Object.values(member.emergency).some((v) => v.trim());

  const value = member[field];

  return value === null || value === '';
}

/** Os rótulos dos campos, para os avisos ("Telefone: mantido o do cadastro"). */
export const FILLABLE_LABEL: Record<FillableField, string> = {
  nickname: 'Nome de apresentação',
  birthDate: 'Data de nascimento',
  email: 'E-mail',
  phone: 'Telefone',
  civilStatus: 'Estado civil',
  profession: 'Profissão',
  churchStatus: 'Situação',
  memberSince: 'Membro desde',
  joinedVia: 'Forma de ingresso',
  baptismDate: 'Data de batismo',
  originChurch: 'Igreja de origem',
  cpf: 'CPF',
  address: 'Endereço',
  emergency: 'Contato de emergência',
};

/**
 * O que a linha da planilha completa em quem já está cadastrado: cada campo preenchido na
 * planilha e vazio no cadastro entra (`fill`); preenchido nos dois e diferente, fica o do
 * cadastro (`kept`, para a tela avisar). Nunca sobrescreve.
 */
export function fillFrom(
  existing: Member,
  imported: ImportedMember
): { fill: MemberFill; kept: FillableField[] } {
  const probe = { ...existing, ...imported, sex: existing.sex } as Member;
  const fill: MemberFill = {};
  const kept: FillableField[] = [];

  for (const field of FILLABLE_FIELDS) {
    if (isBlank(probe, field)) continue;
    if (isBlank(existing, field)) Object.assign(fill, { [field]: imported[field] });
    else if (JSON.stringify(existing[field]) !== JSON.stringify(imported[field])) kept.push(field);
  }

  return { fill, kept };
}

/** Como a disponibilidade escolhida entra: no lugar da atual ou só onde não foi dita. */
export type AvailabilityMode = 'replace' | 'fillUnset';

/** No máximo tantos membros num "Salvar" (o mesmo limite da planilha). */
export const MAX_BULK_EDITS = 1000;

const SLOTS = 21;

/** Habilita (`on`) ou tira o membro da função; o ministério sem função sai do mapa. */
export function withRole(member: Member, ministryId: string, roleId: string, on: boolean): Member {
  const current = member.roles[ministryId] ?? [];

  if (current.includes(roleId) === on) return member;

  const next = on ? [...current, roleId] : current.filter((id) => id !== roleId);
  const { [ministryId]: _, ...others } = member.roles;

  return { ...member, roles: next.length ? { ...others, [ministryId]: next } : others };
}

/**
 * Aplica a grade escolhida: `replace` troca a disponibilidade inteira; `fillUnset` só
 * preenche os períodos que o membro ainda não disse ("não configurado"), sem desfazer o
 * que já estava marcado.
 */
export function withAvailability(
  member: Member,
  grid: readonly AvailState[],
  mode: AvailabilityMode
): Member {
  const availability = member.availability.map((current, i) => {
    const chosen = grid[i] ?? 'unset';

    if (mode === 'replace') return chosen;

    return current === 'unset' ? chosen : current;
  });

  return availability.every((v, i) => v === member.availability[i]) ? member : { ...member, availability };
}

const sameRoles = (a: Record<string, string[]>, b: Record<string, string[]>) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  return [...keys].every((k) => {
    const x = [...(a[k] ?? [])].sort();
    const y = [...(b[k] ?? [])].sort();

    return x.length === y.length && x.every((id, i) => id === y[i]);
  });
};

const sameAvailability = (a: readonly AvailState[], b: readonly AvailState[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** A diferença entre o gravado e o rascunho: só os membros e as partes que mudaram. */
export function memberEdits(saved: readonly Member[], drafts: readonly Member[]): MemberEdit[] {
  const byId = new Map(saved.map((m) => [m.id, m]));

  return drafts.flatMap((draft): MemberEdit[] => {
    const before = byId.get(draft.id);

    if (!before) return [];

    const edit: MemberEdit = { memberId: draft.id };

    if (!sameRoles(before.roles, draft.roles)) edit.roles = draft.roles;
    if (!sameAvailability(before.availability, draft.availability)) edit.availability = draft.availability;

    return edit.roles || edit.availability ? [edit] : [];
  });
}

/** Quantos membros têm alteração no rascunho (o "Salvar N alterações"). */
export function editedCount(saved: readonly Member[], drafts: readonly Member[]): number {
  return memberEdits(saved, drafts).length;
}

/** O membro como fica com a edição (sem conferir nada). */
function edited(member: Member, edit: MemberEdit): Member {
  return {
    ...member,
    ...edit.fill,
    ...(edit.roles ? { roles: edit.roles } : {}),
    ...(edit.availability ? { availability: edit.availability } : {}),
  };
}

/**
 * Confere o lote com as regras do "Salvar" do perfil: cada função que entra ou sai pede
 * `editRole` (o Administrador todas, o Líder de Ministério as do seu, o Líder de Função as
 * que lidera); a disponibilidade pede `editAvailability` — sobre o membro já com as funções
 * do lote, porque quem acabou de entrar na função do líder passa a ser liderado por ele.
 * Membro inativo não ganha função (inativar o tira dos ministérios). Devolve o primeiro
 * problema — `forbidden` quando é de permissão (403 no servidor) —, ou `null`.
 */
export function memberEditError(
  edits: readonly MemberEdit[],
  members: readonly Member[],
  ministries: readonly Ministry[],
  permissions: Permissions
): MemberEditProblem | null {
  const invalid = (message: string): MemberEditProblem => ({ message, forbidden: false });
  const forbidden = (message: string): MemberEditProblem => ({ message, forbidden: true });

  if (edits.length > MAX_BULK_EDITS) return invalid(`No máximo ${MAX_BULK_EDITS} membros de uma vez.`);

  const byId = new Map(members.map((m) => [m.id, m]));
  const roleOwner = new Map(ministries.flatMap((m) => m.roles.map((r) => [r.id, m.id] as const)));
  const seen = new Set<string>();

  for (const edit of edits) {
    const before = byId.get(edit.memberId);

    if (!before) return invalid('Um dos membros não existe mais.');
    if (seen.has(edit.memberId)) return invalid(`${before.name} aparece duas vezes no lote.`);
    seen.add(edit.memberId);

    const after = edited(before, edit);

    if (edit.roles) {
      for (const [ministryId, roleIds] of Object.entries(edit.roles)) {
        if (roleIds.some((id) => roleOwner.get(id) !== ministryId))
          return invalid('Uma das funções não pertence ao ministério informado.');
        if (new Set(roleIds).size !== roleIds.length) return invalid('Função repetida no mesmo ministério.');
      }

      const enabled = (m: Member) => new Set(Object.values(m.roles).flat());
      const was = enabled(before);
      const now = enabled(after);
      const touched = [...now].filter((id) => !was.has(id)).concat([...was].filter((id) => !now.has(id)));

      if (touched.some((id) => !permissions.editRole(id)))
        return forbidden('Você só altera a habilitação nas funções que lidera.');
      if (!isActiveMember(before) && [...now].some((id) => !was.has(id)))
        return invalid(`${before.name} está inativo(a): reative o cadastro antes de habilitar em funções.`);
    }

    if (edit.fill) {
      if (!permissions.manageMembers)
        return forbidden('Só o administrador completa o cadastro pela planilha.');

      const filled = Object.keys(edit.fill) as FillableField[];
      const taken = filled.find(
        (f) => !(FILLABLE_FIELDS as readonly string[]).includes(f) || !isBlank(before, f)
      );

      if (taken)
        return invalid(
          `${FILLABLE_LABEL[taken] ?? taken} de ${before.name} já está preenchido: a planilha só completa o que está vazio.`
        );
    }

    if (edit.availability) {
      if (edit.availability.length !== SLOTS) return invalid('A disponibilidade tem 21 períodos.');
      if (!permissions.editAvailability(after) && !permissions.editAvailability(before))
        return forbidden(`Você não pode alterar a disponibilidade de ${before.name}.`);
    }
  }

  return null;
}

/** Grava o lote nos membros (já conferido por `memberEditError`). */
export function applyMemberEdits(members: readonly Member[], edits: readonly MemberEdit[]): Member[] {
  const byId = new Map(edits.map((e) => [e.memberId, e]));

  return members.map((m) => {
    const edit = byId.get(m.id);

    return edit ? edited(m, edit) : m;
  });
}

/** O "antes" do lote, para desfazer: as mesmas partes, com o valor gravado. */
export function revertEdits(members: readonly Member[], edits: readonly MemberEdit[]): MemberEdit[] {
  const byId = new Map(members.map((m) => [m.id, m]));

  return edits.flatMap((edit): MemberEdit[] => {
    const before = byId.get(edit.memberId);

    if (!before) return [];

    return [
      {
        memberId: edit.memberId,
        ...(edit.roles ? { roles: before.roles } : {}),
        ...(edit.availability ? { availability: before.availability } : {}),
        ...(edit.fill
          ? {
              fill: Object.fromEntries(
                Object.keys(edit.fill).map((f) => [f, before[f as FillableField]])
              ) as MemberFill,
            }
          : {}),
      },
    ];
  });
}

/** Não está habilitado em nenhuma função de nenhum ministério. */
export function withoutMinistry(member: Member): boolean {
  return !Object.values(member.roles).some((ids) => ids.length > 0);
}

/** Nunca disse quando pode servir: os 21 períodos "não configurado". */
export function availabilityUnset(member: Member): boolean {
  return member.availability.every((v) => v === 'unset');
}

/** O que a importação grava: os cadastros novos e o que muda em quem já estava cadastrado. */
export interface ImportPlan {
  created: Member[];
  linked: MemberEdit[];
}

/**
 * As linhas prontas viram o plano: a linha nova cria o membro (id `i{semente}l{linha}`,
 * estável enquanto a tela troca de etapa); a de quem já está cadastrado completa o vazio
 * (`fillFrom`). A etapa "Atribuir" acrescenta funções e disponibilidade aos dois.
 */
export function importPlan(
  members: readonly Member[],
  rows: readonly ImportRow[],
  idSeed: string
): ImportPlan {
  const byId = new Map(members.map((m) => [m.id, m]));
  const created: Member[] = [];
  const linked: MemberEdit[] = [];

  for (const row of rows) {
    if (!row.ok) continue;

    const existing = row.linkTo ? byId.get(row.linkTo) : undefined;

    if (existing) {
      const { fill } = fillFrom(existing, row.member);

      linked.push({ memberId: existing.id, ...(Object.keys(fill).length ? { fill } : {}) });
    } else if (!row.linkTo && row.member.sex) {
      const { sex, ...fields } = row.member;
      const base = newMember(`i${idSeed}l${row.line}`, {
        name: fields.name,
        birthDate: fields.birthDate,
        sex,
      });

      created.push({ ...base, ...fields, name: base.name, nickname: fields.nickname.trim() });
    }
  }

  return { created, linked };
}
